// src/services/OAuth2JLoService.ts

import axios, { type AxiosResponse } from 'axios'
import * as Sentry from '@sentry/vue'
import { AuthStateValidationError, SetStateError } from '@/store/session/exceptions'

// Sentinel value required by the dev-redirect proxy: the proxy contract mandates a `code`
// param on every callback URL, but logout flows have no real auth code, so we pass this
// fixed placeholder so the proxy can recognise and forward the request.
const PROXY_LOGOUT_CODE = '1'

/**
 * Read an optional URL-valued env variable defensively. Deployment tooling can inject
 * garbage instead of leaving the variable empty (e.g. skaffold renders undefined Go
 * template variables as the literal string "<no value>"), and such a truthy value must
 * never activate preview-only auth behaviour. Only accept well-formed HTTPS URLs
 * (HTTP is rejected for VITE_AUTH_PROXY_URL / VITE_JLOO_PREVIEW_URI).
 */
function readOptionalUrlEnv(value: string | undefined): string | undefined {
  const trimmed = value?.trim()
  if (!trimmed) {
    return undefined
  }
  try {
    const url = new URL(trimmed)
    if (url.protocol !== 'https:') {
      return undefined
    }
  } catch {
    return undefined
  }
  return trimmed
}

// ==================== Type Definitions ====================

/**
 * OAuth2 Token Response from the authorization server
 */
export interface TokenResponse {
  access_token: string
  token_type: string
  expires_in: number
  refresh_token: string
  scope?: string
}

/**
 * OAuth2 Authorization Response parameters from redirect
 */
export interface AuthorizeResponse {
  code: string | null
  state: string | null
}

/**
 * Fixed OAuth2 parameters that never change
 */
interface AuthorizeFixedParams {
  client_id: string
  redirect_uri: string
  response_type: 'code'
  code_challenge_method: 'S256'
  l: 'b2b'
}

/**
 * Variable OAuth2 parameters that change per request
 */
interface AuthorizeVariableParams {
  locale: string
  state: string | null
  code_challenge: string | null
}

/**
 * Application state data to preserve across OAuth flow
 */
export interface AppStateData {
  [key: string]: any
}

/**
 * Decoded state from OAuth response
 */
interface DecodedState extends AppStateData {
  CSRFValue: string
}

/**
 * Token stored in localStorage
 */
export interface StoredToken {
  access_token: string
  refresh_token: string
  expires_in: number
  token_type: string
}

// ==================== Service Class ====================

class OAuth2JLoService {
  private readonly authProxyUri: string | undefined
  private readonly redirectTarget: string
  private readonly logoutTargetBase: string
  private readonly clientId: string
  private readonly authorizeEndpoint: string
  private readonly tokenEndpoint: string
  private readonly logoutEndpoint: string
  
  private authorizeResponse: AuthorizeResponse
  private readonly authorizeFixedParams: AuthorizeFixedParams
  private authorizeVariableParams: AuthorizeVariableParams

  constructor() {
    this.authProxyUri = readOptionalUrlEnv(import.meta.env.VITE_AUTH_PROXY_URL)
    const studioClientBase = (import.meta.env.VITE_STUDIO_CLIENT_URL ?? '').replace(/\/$/, '')
    const studioClientHomeBase = studioClientBase.endsWith('/home') ? studioClientBase : `${studioClientBase}/home`
    this.redirectTarget = `${studioClientHomeBase}/auth/login`
    this.logoutTargetBase = `${studioClientHomeBase}/getstarted`

    const oauthBase = readOptionalUrlEnv(import.meta.env.VITE_JLOO_PREVIEW_URI) ?? import.meta.env.VITE_OAUTH_DOMAIN
    this.clientId = import.meta.env.VITE_CLIENT_ID
    this.authorizeEndpoint = `${oauthBase}/server/oauth2/authorize`
    this.tokenEndpoint = `${oauthBase}/server/oauth2/token`
    this.logoutEndpoint = `${oauthBase}/server/logout`

    this.authorizeResponse = {
      code: null,
      state: null,
    }

    this.authorizeFixedParams = {
      client_id: this.clientId,
      redirect_uri: this.authProxyUri || this.redirectTarget,
      response_type: 'code',
      code_challenge_method: 'S256',
      l: "b2b"
    }

    this.authorizeVariableParams = {
      locale: 'en-US',
      state: null,
      code_challenge: null,
    }
  }

  /**
   * Generate OAuth2 authorization URI with PKCE
   * @param locale - User locale (default: 'en-US')
   * @param appStateData - Application state to preserve across OAuth flow
   * @returns Authorization URI to redirect user to
   */
  async generateAuthUri(locale: string = 'en-US', appStateData: AppStateData = {}): Promise<string> {
    this.reset()
    this.authorizeVariableParams.locale = locale
    this.setStateParam(appStateData)
    this.authorizeVariableParams.code_challenge = await this.generateCodeChallenge()
    const uriParams = this.generateUriParams({ ...this.authorizeFixedParams, ...this.authorizeVariableParams })
    return `${this.authorizeEndpoint}?${uriParams}`
  }

  /**
   * Generate logout URI with callback redirect
   * @param previousPath - Path to redirect to after logout
   * @returns Logout URL
   */
  generateLogoutUri(previousPath?: string): URL {
    const url = new URL(this.logoutEndpoint)
    const callbackUrl = this.buildLogoutCallback(previousPath)

    // Branch preview hosts are not registered logout-redirect domains in JLoo, so the
    // `continue` target must go through the dev-redirect-proxy (a registered domain) which
    // forwards back to the dynamic branch URL encoded in `state.target`.
    if (this.authProxyUri) {
      url.searchParams.append('continue', this.buildProxyRedirectUrl(callbackUrl))
    } else {
      url.searchParams.append('continue', callbackUrl)
    }
    return url
  }

  /**
   * Request access token using authorization code
   * @param locale - User locale (default: 'en-US')
   * @returns Token response or undefined on error
   */
  async requestToken(locale: string = 'en-US'): Promise<TokenResponse | undefined> {
    try {
      const codeVerifier = window.localStorage.getItem('code_verifier')
      if (!codeVerifier) {
        throw new Error('Code verifier not found in localStorage')
      }
      
      window.localStorage.removeItem('code_verifier')
      const requestUri = `${this.tokenEndpoint}?locale=${locale}`
      
      const result: AxiosResponse<TokenResponse> = await axios.post(
        requestUri,
        new URLSearchParams({
          grant_type: 'authorization_code',
          client_id: this.clientId,
          code: this.authorizeResponse.code || '',
          code_verifier: codeVerifier,
          redirect_uri: this.authorizeFixedParams.redirect_uri,
        }),
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        }
      )
      
      return result?.data
    } catch (error) {
      Sentry.withScope(scope => {
        scope.setTag('feature', 'OAuth2JLoService')
        Sentry.captureException(error)
      })
      return undefined
    }
  }

  /**
   * Refresh access token using refresh token
   * @param refreshToken - Refresh token from previous authentication
   * @returns New token response
   */
  async requestTokenRefresh(refreshToken: string): Promise<TokenResponse> {
    const result: AxiosResponse<TokenResponse> = await axios.post(
      this.tokenEndpoint,
      new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: this.clientId,
        refresh_token: refreshToken,
      }),
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      }
    )
    
    return result?.data
  }

  /**
   * Set state parameter with CSRF protection
   * @param appStateData - Application state to encode in state parameter
   * @throws SetStateError if encoding fails
   */
  private setStateParam(appStateData: AppStateData): void {
    let CSRFValue: string | null = null
    try {
      CSRFValue = this.generateRandomString(20)
      const stateData: DecodedState = { ...appStateData, CSRFValue }
      if (this.authProxyUri) {
        stateData['target'] = this.redirectTarget
      }
      const b64State = window.btoa(JSON.stringify(stateData))
      window.localStorage.setItem('state', b64State)
      this.authorizeVariableParams.state = window.encodeURIComponent(b64State)
    } catch (error) {
      throw new SetStateError(error, { appStateData, CSRFValue })
    }
  }

  /**
   * Parse and validate authorization response from OAuth redirect
   * @param responseUrl - Full URL with OAuth response parameters
   * @returns Decoded application state
   * @throws AuthStateValidationError if state validation fails
   */
  getAuthorizeResponse(responseUrl: string): AppStateData {
    const parsedUrl = new URL(responseUrl)
    this.authorizeResponse.code = parsedUrl.searchParams.get('code')
    this.authorizeResponse.state = parsedUrl.searchParams.get('state')
    
    const savedState = window.localStorage.getItem('state')
    window.localStorage.removeItem('state')
    
    if (savedState === this.authorizeResponse.state) {
      const state: DecodedState = JSON.parse(
        window.atob(window.decodeURIComponent(this.authorizeResponse.state || ''))
      )
      return state
    } else {
      throw new AuthStateValidationError({ 
        savedState, 
        responseState: this.authorizeResponse.state 
      })
    }
  }

  /**
   * Generate PKCE code challenge from random verifier
   * @returns Base64URL encoded SHA-256 hash of code verifier
   */
  private async generateCodeChallenge(): Promise<string | undefined> {
    try {
      const codeVerifier = this.generateRandomString()
      window.localStorage.setItem('code_verifier', codeVerifier)
      const codeDigested = await this.digestSHA256(codeVerifier)
      
      return window
        .btoa(String.fromCharCode(...new Uint8Array(codeDigested)))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '')
    } catch (error) {
      Sentry.withScope(scope => {
        scope.setTag('feature', 'OAuth2JLoService')
        Sentry.captureException(error)
      })
      return undefined
    }
  }

  /**
   * Reset OAuth state and clear localStorage
   */
  reset(): void {
    this.authorizeResponse.code = null
    this.authorizeResponse.state = null
    window.localStorage.removeItem('state')
    window.localStorage.removeItem('code_verifier')
    window.localStorage.removeItem('jlo_token')
    window.localStorage.removeItem('studio_token')
    window.localStorage.removeItem('fromDeepLink')
  }

  /**
   * Get token details from localStorage
   * @returns Stored token or null if not found or parse error
   */
  getTokenFromLocalStorage(): StoredToken | null {
    try {
      const result = window.localStorage.getItem('jlo_token')
      
      if (result) {
        return JSON.parse(result) as StoredToken
      }
      
      return null
    } catch (error) {
      Sentry.withScope(scope => {
        scope.setTag('feature', 'OAuth2JLoService')
        Sentry.captureException(error)
      })
      return null
    }
  }

  /**
   * Hash plaintext using SHA-256
   * @param plainText - Text to hash
   * @returns ArrayBuffer containing hash
   */
  private digestSHA256(plainText: string): Promise<ArrayBuffer> {
    const encodedString = new TextEncoder().encode(plainText)
    return window.crypto.subtle.digest('SHA-256', encodedString)
  }

  /**
   * Generate cryptographically secure random string
   * @param length - Desired length (default: 50)
   * @returns Random hex string
   */
  private generateRandomString(length: number = 50): string {
    const typedArray = new Uint8Array(Math.floor(length / 2))
    window.crypto.getRandomValues(typedArray)
    const strArray = Array.from(typedArray, num => num.toString(16).padStart(2, '0'))
    return strArray.join('')
  }

  private buildLogoutCallback(previousPath?: string): string {
    const url = new URL(this.logoutTargetBase)
    if (previousPath) {
      url.searchParams.set('redirectFrom', previousPath)
    }
    return url.toString()
  }

  private buildProxyRedirectUrl(targetUrl: string): string {
    const proxyUrl = new URL(this.authProxyUri)
    const encodedState = window
      .btoa(JSON.stringify({ target: targetUrl }))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '')
    proxyUrl.searchParams.set('code', PROXY_LOGOUT_CODE)
    proxyUrl.searchParams.set('state', encodedState)
    return proxyUrl.toString()
  }

  /**
   * Convert object to URI query parameters
   * @param params - Parameters to encode
   * @returns URI query string
   */
private generateUriParams(params: Record<string, string | null>): string {
  return Object.entries(params)
    .filter(([, value]) => value !== null)  
    .reduce((acc, [key, value]) => `${acc}&${key}=${value}`, '')
    .slice(1)
}
}

export default new OAuth2JLoService()